import { logger } from '../lib/logger';
import type { SupportedLanguages } from '../i18n';

/**
 * Format a duration in minutes to a human-readable string based on the language
 * 
 * @param minutes The duration in minutes
 * @param language The language to format for
 * @returns Formatted duration string (e.g., "1h 7min" or "1:07" depending on language)
 */
export function formatDuration(
  minutes: number | string | undefined,
  language: SupportedLanguages = 'fr'
): string {
  // Handle undefined, null, or empty string
  if (minutes === undefined || minutes === null || minutes === '') {
    return '';
  }

  // Convert to number if it's a string
  const mins = typeof minutes === 'string' ? parseInt(minutes, 10) : minutes;

  // Handle NaN
  if (isNaN(mins)) {
    return '';
  }

  // If less than 60 minutes, just show minutes
  if (mins < 60) {
    return `${mins}min`;
  }

  // Calculate hours and remaining minutes
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;

  // Format based on language
  switch (language) {
    case 'en':
      // English format: 1h 7m or 1:07
      return remainingMins === 0 
        ? `${hours}h` 
        : `${hours}h ${remainingMins}m`;
    
    case 'ja':
    case 'zh':
      // Japanese/Chinese format: 1時間7分
      return remainingMins === 0 
        ? `${hours}時間` 
        : `${hours}時間${remainingMins}分`;
    
    case 'de':
      // German format: 1 Std. 7 Min.
      return remainingMins === 0 
        ? `${hours} Std.` 
        : `${hours} Std. ${remainingMins} Min.`;
    
    case 'ru':
      // Russian format with proper pluralization (simplified)
      return remainingMins === 0 
        ? `${hours} ч` 
        : `${hours} ч ${remainingMins} мин`;
    
    default:
      // Default format (French, Spanish, Italian, etc.): 1h 7min
      return remainingMins === 0 
        ? `${hours}h` 
        : `${hours}h ${remainingMins}min`;
  }
}

/**
 * Format a time duration for travel (walking, biking, driving)
 * 
 * @param minutes The duration in minutes
 * @param language The language to format for
 * @returns Formatted travel time string
 */
export function formatTravelTime(
  minutes: number | string | undefined,
  language: SupportedLanguages = 'fr'
): string {
  return formatDuration(minutes, language);
}

/**
 * Format an event duration
 * 
 * @param minutes The duration in minutes
 * @param language The language to format for
 * @returns Formatted event duration string
 */
export function formatEventDuration(
  minutes: number | string | undefined,
  language: SupportedLanguages = 'fr'
): string {
  if (!minutes) return '';
  
  const formatted = formatDuration(minutes, language);
  
  // For some languages, we might want to add additional text
  switch (language) {
    case 'en':
      return `Duration: ${formatted}`;
    case 'fr':
      return `Durée: ${formatted}`;
    case 'es':
      return `Duración: ${formatted}`;
    case 'it':
      return `Durata: ${formatted}`;
    case 'de':
      return `Dauer: ${formatted}`;
    default:
      return formatted;
  }
}

/**
 * Format a time range (start time - end time)
 * 
 * @param startTime Start time in HH:MM format
 * @param endTime End time in HH:MM format
 * @param language The language to format for
 * @returns Formatted time range string
 */
export function formatTimeRange(
  startTime: string,
  endTime: string,
  language: SupportedLanguages = 'fr'
): string {
  if (!startTime || !endTime) return startTime || endTime || '';
  
  try {
    // Create Date objects for formatting
    const startDate = new Date(`2000-01-01T${startTime}`);
    const endDate = new Date(`2000-01-01T${endTime}`);
    
    // Format options
    const options: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: 'numeric',
      hour12: language === 'en' // Use 12-hour format for English
    };
    
    const formattedStart = new Intl.DateTimeFormat(language, options).format(startDate);
    const formattedEnd = new Intl.DateTimeFormat(language, options).format(endDate);
    
    // Different separators based on language
    const separator = language === 'ja' || language === 'zh' ? '～' : ' - ';
    
    return `${formattedStart}${separator}${formattedEnd}`;
  } catch (error) {
    logger.error('Error formatting time range', {
      category: 'Formatting',
      data: { error, startTime, endTime }
    });
    return `${startTime} - ${endTime}`;
  }
}